<template>
    <div class="uk-card uk-card-alternative uk-padding share-card">
        <div class="uk-grid uk-grid-large uk-flex-middle" uk-grid>
            <div class="uk-width-expand@m">
                <h2 class="share-title">Lauf weitersagen</h2>
                <p class="share-text">
                    Je mehr mitlaufen, desto mehr Kilometer kommen zusammen.
                    Teile den Link oder lass den QR-Code scannen.
                </p>

                <div class="share-buttons">
                    <button
                        v-if="canNativeShare"
                        type="button"
                        class="share-btn is-primary"
                        @click="nativeShare">
                        <span uk-icon="icon: forward; ratio: 0.9" />
                        Teilen
                    </button>

                    <a
                        v-for="target in targets"
                        :key="target.label"
                        :href="target.href"
                        class="share-btn"
                        target="_blank"
                        rel="noopener nofollow"
                        :aria-label="target.label"
                        :uk-tooltip="'title:' + target.label">
                        <span
                            :uk-icon="'icon: ' + target.icon + '; ratio: 0.9'" />
                    </a>

                    <button
                        type="button"
                        class="share-btn"
                        :aria-label="copied ? 'Link kopiert' : 'Link kopieren'"
                        :uk-tooltip="'title:Link kopieren'"
                        @click="copyLink">
                        <span
                            :uk-icon="
                                'icon: ' +
                                (copied ? 'check' : 'link') +
                                '; ratio: 0.9'
                            " />
                    </button>
                </div>

                <!-- Immer im Layout, nur aus- und eingeblendet: sonst springt
                     die Buttonreihe beim Kopieren um eine Zeile nach oben. -->
                <div class="share-copied" :class="{visible: copied}">
                    Link kopiert.
                </div>
            </div>

            <div class="uk-width-auto@m uk-text-center">
                <div ref="qrcode" class="qr-code" />
                <a href="#" class="qr-download" @click.prevent="downloadQr">
                    QR-Code herunterladen
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import QRCode from 'easyqrcodejs';

export default {
    name: 'CharityRunShare',
    props: {
        slug: {
            type: String,
            required: true,
        },
        name: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            qrCode: null,
            copied: false,
            copiedTimer: null,
            // navigator.share gibt es nur im sicheren Kontext und auf den
            // meisten Desktop-Browsern gar nicht – die einzelnen Buttons
            // bleiben deshalb der Normalfall, nicht der Fallback.
            canNativeShare:
                typeof navigator !== 'undefined' && !!navigator.share,
        };
    },
    computed: {
        // Nie window.location: auf localhost käme dabei ein Link heraus, den
        // niemand sonst öffnen kann. getBaseUrl zeigt immer auf die Live-Domain.
        shareUrl() {
            return this.getBaseUrl + '/charity-run/' + this.slug;
        },
        shareText() {
            return (
                this.name + ' – lauf mit und unterstütze Cancel Cancer e. V.'
            );
        },
        targets() {
            const url = encodeURIComponent(this.shareUrl);
            const text = encodeURIComponent(this.shareText);

            return [
                {
                    label: 'WhatsApp',
                    icon: 'whatsapp',
                    href:
                        'https://wa.me/?text=' +
                        encodeURIComponent(
                            this.shareText + ' ' + this.shareUrl
                        ),
                },
                {
                    label: 'Facebook',
                    icon: 'facebook',
                    href:
                        'https://www.facebook.com/sharer/sharer.php?u=' + url,
                },
                {
                    label: 'LinkedIn',
                    icon: 'linkedin',
                    href:
                        'https://www.linkedin.com/sharing/share-offsite/?url=' +
                        url,
                },
                {
                    label: 'X',
                    icon: 'x',
                    href:
                        'https://twitter.com/intent/tweet?text=' +
                        text +
                        '&url=' +
                        url,
                },
                {
                    label: 'E-Mail',
                    icon: 'mail',
                    href:
                        'mailto:?subject=' +
                        encodeURIComponent(this.name) +
                        '&body=' +
                        encodeURIComponent(
                            this.shareText + '\n\n' + this.shareUrl
                        ),
                },
            ];
        },
    },
    mounted() {
        this.renderQr();
    },
    beforeDestroy() {
        clearTimeout(this.copiedTimer);
    },
    methods: {
        renderQr() {
            this.qrCode = new QRCode(this.$refs.qrcode, {
                text: this.shareUrl,
                // Doppelt so groß gezeichnet wie angezeigt – easyqrcodejs malt
                // in Canvas-Pixeln, und 240px sind auf einem Handy sichtbar
                // ausgefranst.
                width: 440,
                height: 440,
                quietZone: 24,
                quietZoneColor: '#ffffff',
                colorDark: '#121217',
                colorLight: '#ffffff',
                correctLevel: QRCode.CorrectLevel.H,
                // Das Logo bringt seine eigene dunkle Kachel mit. logoBackground
                // Color füllt exakt das Container-Rechteck, wodurch das breite
                // Lockup links und rechts am Rand klebt.
                logo: '/assets/qr-logo.png',
                logoWidth: 200,
                logoHeight: 108,
                logoBackgroundTransparent: true,
            });
        },
        downloadQr() {
            this.qrCode.download('Cancel Cancer ' + this.name + ' QR-Code');
        },
        nativeShare() {
            navigator
                .share({
                    title: this.name,
                    text: this.shareText,
                    url: this.shareUrl,
                })
                // Bricht der Nutzer das Systemdialog ab, ist das ein
                // AbortError – kein Fehler, über den zu berichten wäre.
                .catch(() => {});
        },
        copyLink() {
            this.$copyText(this.shareUrl).then(() => {
                this.copied = true;
                clearTimeout(this.copiedTimer);
                this.copiedTimer = setTimeout(() => {
                    this.copied = false;
                }, 2500);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.share-card {
    border-radius: 8px;
}

.share-title {
    font-size: 1.6rem;
    font-weight: 600;
    margin: 0 0 12px;
    color: #ddd;
}

.share-text {
    font-size: 15px;
    line-height: 1.6;
    opacity: 0.85;
    margin: 0 0 20px;
}

.share-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
}

.share-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 44px;
    min-width: 44px;
    padding: 0 14px;
    justify-content: center;
    background: #1e1e26;
    border: 1px solid #333;
    border-radius: 50px;
    color: #fff !important;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none !important;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;

    &:hover {
        border-color: #3ddc97;
        background: #23232c;
    }

    &.is-primary {
        background: #3ddc97;
        border-color: #3ddc97;
        color: #121217 !important;

        &:hover {
            background: #2cb87e;
            border-color: #2cb87e;
        }
    }
}

.share-copied {
    font-size: 12px;
    color: #3ddc97;
    margin-top: 10px;
    opacity: 0;
    transition: opacity 0.2s;

    &.visible {
        opacity: 1;
    }
}

.qr-code ::v-deep canvas {
    display: block;
    width: 100%;
    height: auto;
    max-width: 220px;
    margin: 0 auto;
    border-radius: 8px;
}

.qr-download {
    display: inline-block;
    margin-top: 10px;
    font-size: 12px;
    color: #3ddc97 !important;
    font-weight: 600;
    text-decoration: underline !important;
}
</style>
